<template>
    <div v-bind="$attrs" class="flex flex-col w-full h-full overflow-hidden text-xs">
        <div class="flex-1 overflow-y-auto p-2 space-y-1">

            <!-- trigger header -->
            <div class="flex items-center gap-2 px-3 py-2 rounded-md trigger-header">
                <span class="text-sm">⚡</span>
                <span class="font-semibold text-ui-active">{{ TRIGGER_LABELS[scriptName] ?? scriptName }}</span>
                <span class="text-ui-active/60 text-[10px]">— {{ TRIGGER_HINTS[scriptName] ?? '' }}</span>
            </div>

            <!-- command rows -->
            <template v-for="(row, index) in orderedRows" :key="row.id">
                <!-- drop insertion line -->
                <div v-if="dropTarget === index && dropTarget !== draggingIndex"
                    class="h-0.5 rounded-full bg-ui-active mx-1 my-0.5" />

                <div
                    :data-row-index="index"
                    class="group"
                    :class="draggingIndex === index ? 'cmd-card cmd-card--active' : 'cmd-card'">

                <!-- drag handle — only mousedown here starts a reorder -->
                <span
                    @mousedown.prevent="startDrag(index, $event)"
                    class="text-slate-600 hover:text-slate-400 cursor-grab pt-0.5 select-none">⠿</span>

                <!-- type selector styled as coloured badge -->
                <div class="flex-shrink-0 pt-0.5">
                    <select :value="paletteValue(row)" @change="onTypeChange(row, $event.target.value)"
                        class="text-[10px] rounded px-1.5 py-0.5 border cursor-pointer font-medium"
                        :style="{ borderColor: typeColor(row.type)+'88', color: typeColor(row.type), background: typeColor(row.type)+'18' }">
                        <option v-for="t in allTypes" :key="t.paletteId ?? t.type" :value="t.paletteId ?? t.type"
                            style="color:#e2e8f0;background:var(--ui-bg-input)">{{ t.icon }} {{ t.label }}</option>
                    </select>
                </div>

                <!-- inline fields -->
                <div class="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">

                    <template v-if="row.type === 'backlight'">
                        <select :value="row.data.state !== false ? '1' : '0'"
                            @change="set(row, 'state', $event.target.value === '1')" class="w-14">
                            <option value="1">On</option><option value="0">Off</option>
                        </select>
                        <input type="range" min="1" max="255" :value="row.data.brightness ?? 128"
                            @input="set(row, 'brightness', parseInt($event.target.value))"
                            class="flex-1 h-1 accent-yellow-400" style="min-width:80px" />
                        <span class="text-gray-400 w-7 text-right">{{ row.data.brightness ?? 128 }}</span>
                    </template>

                    <template v-else-if="row.type === 'moodlight'">
                        <select :value="row.data.state !== false ? '1' : '0'"
                            @change="set(row, 'state', $event.target.value === '1')" class="w-14">
                            <option value="1">On</option><option value="0">Off</option>
                        </select>
                        <input type="color" class="color-pick"
                            :value="moodHex(row.data)"
                            @input="onMoodColor(row, $event.target.value)" />
                        <span class="font-mono text-[10px] text-gray-400">{{ moodHex(row.data) }}</span>
                    </template>

                    <template v-else-if="row.type === 'gotoPage'">
                        <select :value="row.data.pageTarget"
                            @change="set(row, 'pageTarget', $event.target.value)" class="flex-1">
                            <option value="prev">← Previous</option>
                            <option value="next">→ Next</option>
                            <option value="back">↩ Back</option>
                            <option v-for="p in pages" :key="p.id" :value="String(p.id)">{{ p.label }}</option>
                        </select>
                    </template>

                    <template v-else-if="row.type === 'clearPage'">
                        <select :value="row.data.page"
                            @change="set(row, 'page', $event.target.value)" class="flex-1">
                            <option value="all">All pages</option>
                            <option v-for="p in pages" :key="p.id" :value="String(p.id)">{{ p.label }}</option>
                        </select>
                    </template>

                    <template v-else-if="row.type === 'setProperty'">
                        <select :value="row.data.pageId" @change="onSPPage(row, $event.target.value)" class="w-20">
                            <option value="">page</option>
                            <option v-for="p in pages" :key="p.id" :value="p.id">{{ p.label }}</option>
                        </select>
                        <select :value="row.data.objectId" @change="onSPObj(row, $event.target.value)" class="w-24">
                            <option value="">object</option>
                            <option v-for="o in objsOnPage(row.data.pageId)" :key="o.id" :value="o.id">{{ o.label }}</option>
                        </select>
                        <select :value="row.data.property" @change="onSPProp(row, $event.target.value)" class="w-24">
                            <option value="">property</option>
                            <option v-for="p in propsFor(row.data.pageId, row.data.objectId)" :key="p.name" :value="p.name">{{ p.name }}</option>
                        </select>
                        <template v-if="isColorProp(row.data.property)">
                            <input type="color" class="color-pick"
                                :value="safeHex(row.data.value)"
                                @input="set(row, 'value', $event.target.value)" />
                            <input type="text" :value="row.data.value ?? ''"
                                @input="set(row, 'value', $event.target.value)"
                                placeholder="#RRGGBB" class="w-20 font-mono text-[10px]" />
                        </template>
                        <template v-else-if="spType(row.data.property) === 'text'">
                            <input type="text"
                                :value="row.data.value ?? ''"
                                @input="set(row, 'value', $event.target.value)"
                                placeholder="value" class="flex-1" style="min-width:60px" />
                            <button
                                class="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] border leading-tight"
                                style="background:rgba(255,255,255,0.08);border-color:rgba(255,255,255,0.2);color:#cbd5e1"
                                title="String builder / icon picker"
                                @click="openPicker(row)">⌨</button>
                        </template>
                        <input v-else :type="spType(row.data.property)"
                            :value="row.data.value ?? ''"
                            @input="set(row, 'value', $event.target.value)"
                            placeholder="value" class="flex-1" style="min-width:60px" />
                    </template>

                    <template v-else-if="row.type === 'templateRef'">
                        <select :value="row.data.templatePageId ?? ''"
                            @change="onTpPage(row, $event.target.value)" class="flex-1">
                            <option value="">-- template page --</option>
                            <option v-for="t in templatePages" :key="t.id" :value="t.id">{{ t.label }}</option>
                        </select>
                        <select v-if="row.data.templatePageId !== '' && row.data.templatePageId !== undefined"
                            :value="row.data.templateObjectId ?? ''"
                            @change="set(row, 'templateObjectId', toId($event.target.value))" class="flex-1">
                            <option value="">All objects</option>
                            <option v-for="o in objsOnPage(row.data.templatePageId)" :key="o.id" :value="o.id">{{ o.label }}</option>
                        </select>
                        <span v-else class="text-[10px] text-gray-600 italic">no template selected</span>
                    </template>

                    <template v-else-if="row.type === 'delay'">
                        <input type="number" min="0" step="50" :value="row.data.duration ?? 500"
                            @input="set(row, 'duration', parseInt($event.target.value) || 0)" class="w-24" />
                        <span class="text-gray-500 text-[10px]">ms</span>
                    </template>

                    <template v-else-if="row.type === 'runScript'">
                        <input type="text" :value="row.data.filename ?? ''"
                            @input="set(row, 'filename', $event.target.value)"
                            placeholder="/custom.cmd" class="flex-1" />
                    </template>

                    <template v-else-if="row.type === 'idle'">
                        <select :value="row.data.idleState ?? 'off'"
                            @change="set(row, 'idleState', $event.target.value)" class="flex-1">
                            <option value="off">Off (wake screen)</option>
                            <option value="short">Short idle</option>
                            <option value="long">Long idle</option>
                        </select>
                    </template>

                    <template v-else-if="row.type === 'output'">
                        <span class="text-gray-500 text-[10px]">Pin</span>
                        <input type="number" min="0" max="39" :value="row.data.outputPin ?? 0"
                            @input="set(row, 'outputPin', parseInt($event.target.value) || 0)" class="w-14" />
                        <select :value="row.data.state !== false ? '1' : '0'"
                            @change="set(row, 'state', $event.target.value === '1')" class="w-16">
                            <option value="1">High</option><option value="0">Low</option>
                        </select>
                    </template>

                    <template v-else-if="row.type === 'jsonl'">
                        <textarea rows="2" :value="row.data.payload ?? ''"
                            @input="set(row, 'payload', $event.target.value)"
                            @change="set(row, 'payload', $event.target.value)"
                            placeholder='{"page":0,"id":1,"obj":"btn","text":"Hi"}'
                            class="flex-1 font-mono resize-none"
                            style="background:var(--ui-bg-input);color:var(--ui-active);border:1px solid rgba(255,255,255,0.2);border-radius:4px;padding:4px;font-size:10px;min-width:140px" />
                    </template>

                    <template v-else-if="row.type === 'command'">
                        <template v-for="parameter in commandDefinition(row)?.parameters ?? []" :key="parameter.id">
                            <span class="text-gray-500 text-[10px]">{{ parameter.label }}</span>
                            <input type="text"
                                :value="row.data.parameters?.[parameter.id] ?? ''"
                                @input="setCommandParameter(row, parameter.id, $event.target.value)"
                                :placeholder="parameter.scriptDefault ?? parameter.default ?? ''"
                                class="flex-1" style="min-width:80px" />
                        </template>
                        <span v-if="!(commandDefinition(row)?.parameters?.length)"
                            class="text-gray-600 text-[10px] italic">No options</span>
                    </template>

                    <template v-else>
                        <!-- reboot, screenshot — no configurable fields -->
                        <span class="text-gray-600 text-[10px] italic">{{ row.type }}</span>
                    </template>
                </div>

                <!-- delete -->
                <button @click="removeRow(index)"
                    class="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 text-base leading-none mt-0.5 px-1">×</button>
                </div>
            </template>

            <!-- insertion line after last row -->
            <div v-if="dropTarget === orderedRows.length && dropTarget !== draggingIndex"
                class="h-0.5 rounded-full bg-ui-active mx-1 my-0.5" />

            <!-- add command -->
            <select @change="addRow($event)"
                class="w-full mt-1 text-xs cursor-pointer add-cmd-select">
                <option value="">+ Add command…</option>
                <optgroup v-for="cat in NODE_CATEGORIES" :key="cat.label" :label="cat.label">
                    <option v-for="n in cat.nodes" :key="n.paletteId ?? n.type" :value="n.paletteId ?? n.type"
                        style="color:#e2e8f0;background:var(--ui-bg-input)">{{ n.icon }}  {{ n.label }}</option>
                </optgroup>
            </select>
        </div>
    </div>

    <!-- string builder / icon picker — always mounted so the modelValue false→true transition triggers the watcher that seeds editableText -->
    <IconPicker
        v-model="showPicker"
        :initialText="pickerRow?.data?.value ?? ''"
        :availableTabs="pickerRow?.data?.property === 'template' ? ['datetime'] : ['icons', 'variables']"
        @text-updated="onPickerUpdate" />

    <!-- drag ghost — follows the cursor, pointer-events:none so elementFromPoint still works -->
    <Teleport to="body">
        <div v-if="draggingIndex !== null && ghostRow"
            class="fixed z-[9999] pointer-events-none px-3 py-2 rounded-lg border border-ui-active bg-ui-bg shadow-xl shadow-black/60 text-xs flex items-center gap-2 opacity-90"
            :style="{ left: ghostX + 'px', top: ghostY + 'px', transform: 'translate(12px, -50%)' }">
            <span class="select-none font-medium"
                :style="{ color: typeColor(ghostRow.type) }">
                {{ typeIcon(ghostRow.type) }} {{ typeLabel(ghostRow.type, ghostRow.data) }}
            </span>
            <span class="text-gray-400 text-[10px] max-w-[160px] truncate">{{ ghostSummary }}</span>
        </div>
    </Teleport>
</template>

<script setup>
import { inject, computed, ref, onUnmounted } from 'vue';
defineOptions({ inheritAttrs: false });
onUnmounted(() => {
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup',   onDragEnd);
});
import { v4 as uuidv4 } from 'uuid';
import { NODE_CATEGORIES } from './nodePalette.js';
import { getCommandDefinition } from '../actions/commandRegistry.js';
import IconPicker from '../components/IconPicker.vue';

const props = defineProps({
    flow:       { type: Object, required: true }, // { nodes, edges } — mutated directly
    scriptName: { type: String, default: '' },
});

// --- injected context (provided by ScriptsTab)
const haspObjects = inject('haspObjects', null);
const pages         = computed(() => haspObjects?.pages?.value ?? []);
const templatePages = computed(() => haspObjects?.templatePages?.value ?? []);
const objsOnPage  = (pid) => pid !== '' && pid !== undefined && haspObjects
    ? haspObjects.objectsOnPage(parseInt(pid)) : [];
const propsFor    = (pid, oid) => pid !== '' && pid !== undefined && oid !== '' && oid !== undefined && haspObjects
    ? haspObjects.propertiesForObject(parseInt(pid), parseInt(oid)) : [];

// --- trigger metadata
const TRIGGER_LABELS = { boot:'Boot', online:'Online', offline:'Offline', idle_off:'Idle Off', idle_short:'Idle Short', idle_long:'Idle Long' };
const TRIGGER_HINTS  = { boot:'device starts up', online:'WiFi connects', offline:'WiFi disconnects', idle_off:'screen wakes from idle', idle_short:'short idle starts', idle_long:'long idle starts' };

// --- type palette helpers
const allTypes     = NODE_CATEGORIES.flatMap(cat => cat.nodes.map(n => ({ ...n, color: cat.color })));
const typeColorMap = Object.fromEntries(allTypes.map(t => [t.type, t.color]));
const typeDefaults = Object.fromEntries(allTypes.map(t => [t.type, t.defaultData ?? {}]));
const typeColor    = (type) => typeColorMap[type] ?? '#475569';

// --- derive ordered command rows by walking the edge chain
const orderedRows = computed(() => {
    const nodes = props.flow?.nodes ?? [];
    const edges = props.flow?.edges ?? [];
    const nextMap  = Object.fromEntries(edges.map(e => [e.source, e.target]));
    const hasIncoming = new Set(edges.map(e => e.target));
    const trigger  = nodes.find(n => n.type === 'trigger' && !hasIncoming.has(n.id));
    const rows = [];
    let cur = trigger ? nextMap[trigger.id] : null;
    const seen = new Set();
    while (cur && !seen.has(cur)) {
        seen.add(cur);
        const node = nodes.find(n => n.id === cur);
        if (node) rows.push(node);
        cur = nextMap[cur];
    }
    return rows;
});

// --- rebuild edges to match an ordered list of node ids
function rebuildEdges(orderedIds) {
    const trigger = props.flow.nodes.find(n => n.type === 'trigger');
    const chain   = trigger ? [trigger.id, ...orderedIds] : orderedIds;
    const base    = { animated: true, style: { stroke: '#94a3b8', strokeWidth: 2 } };
    props.flow.edges = chain.slice(0, -1).map((id, i) => ({
        ...base,
        id: `e-${id}-${chain[i + 1]}`,
        source: id,           sourceHandle: 'output',
        target: chain[i + 1], targetHandle: 'input',
    }));
}

// --- data mutation (direct — no VueFlow context needed in table view)
function set(row, key, val) {
    row.data = { ...row.data, [key]: val };
}

function onTypeChange(row, newType) {
    const definition = allTypes.find(type => (type.paletteId ?? type.type) === newType);
    row.type = definition?.type ?? newType;
    row.data = { ...(definition?.defaultData ?? typeDefaults[newType] ?? {}) };
}

// --- add / remove rows
function addRow(event) {
    const paletteId = event.target.value;
    if (!paletteId || !props.flow) return;
    event.target.value = '';
    const definition = allTypes.find(type => (type.paletteId ?? type.type) === paletteId);
    const type = definition?.type ?? paletteId;
    const node = {
        id:       `${type}-${uuidv4().slice(0, 8)}`,
        type,
        position: { x: (orderedRows.value.length + 1) * 220, y: 100 },
        data:     { ...(definition?.defaultData ?? typeDefaults[type] ?? {}) },
    };
    props.flow.nodes.push(node);
    rebuildEdges([...orderedRows.value.map(r => r.id), node.id]);
}

function removeRow(index) {
    const row = orderedRows.value[index];
    if (!row) return;
    const idx = props.flow.nodes.findIndex(n => n.id === row.id);
    if (idx >= 0) props.flow.nodes.splice(idx, 1);
    rebuildEdges(orderedRows.value.filter((_, i) => i !== index).map(r => r.id));
}

// --- drag-to-reorder (mouse-based — HTML5 DnD is unreliable in Tauri/WebView)
const draggingIndex = ref(null);
const dropTarget    = ref(null);
const ghostRow      = ref(null);
const ghostX        = ref(0);
const ghostY        = ref(0);

const typeIcon  = (t) => allTypes.find(a => a.type === t)?.icon  ?? '◉';
const typeLabel = (t, data = null) => allTypes.find(a => a.type === t && (!data?.commandId || a.commandId === data.commandId))?.label ?? t;
const paletteValue = row => row.type === 'command' ? `command:${row.data.commandId}` : row.type;
const commandDefinition = row => getCommandDefinition(row.data.commandId);
function setCommandParameter(row, parameterId, value) {
    row.data = { ...row.data, parameters: { ...(row.data.parameters ?? {}), [parameterId]: value } };
}

const ghostSummary = computed(() => {
    const d = ghostRow.value?.data ?? {};
    switch (ghostRow.value?.type) {
        case 'templateRef':  return d.templatePageId ? `page ${d.templatePageId}` : '';
        case 'backlight':    return `${d.state !== false ? 'on' : 'off'} · ${d.brightness ?? 128}`;
        case 'moodlight':    return moodHex(d);
        case 'gotoPage':     return d.pageTarget ?? '';
        case 'clearPage':    return d.page ?? 'all';
        case 'setProperty':  return d.property ? `b${d.objectId}.${d.property}=${d.value ?? ''}` : '';
        case 'delay':        return `${d.duration ?? 500} ms`;
        case 'runScript':    return d.filename ?? '';
        case 'idle':         return d.idleState ?? 'off';
        case 'output':       return `pin ${d.outputPin ?? 0} → ${d.state !== false ? 'high' : 'low'}`;
        case 'jsonl':        return (d.payload ?? '').slice(0, 40);
        case 'command':      return commandDefinition(ghostRow.value)?.label ?? '';
        default:             return '';
    }
});

function startDrag(index, event) {
    draggingIndex.value = index;
    ghostRow.value      = orderedRows.value[index];
    ghostX.value        = event.clientX;
    ghostY.value        = event.clientY;
    document.body.style.cursor = 'grabbing';
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup',   onDragEnd);
}

function onDragMove(event) {
    ghostX.value = event.clientX;
    ghostY.value = event.clientY;

    const el  = document.elementFromPoint(event.clientX, event.clientY);
    const row = el?.closest('[data-row-index]');
    if (row) {
        const rect = row.getBoundingClientRect();
        const idx  = parseInt(row.dataset.rowIndex);
        dropTarget.value = event.clientY < rect.top + rect.height / 2 ? idx : idx + 1;
    } else {
        dropTarget.value = null;
    }
}

function onDragEnd() {
    document.body.style.cursor = '';
    if (draggingIndex.value !== null && dropTarget.value !== null) {
        const src = draggingIndex.value;
        let   dst = dropTarget.value;
        if (dst > src) dst--;
        if (src !== dst) {
            const rows = [...orderedRows.value];
            const [item] = rows.splice(src, 1);
            rows.splice(dst, 0, item);
            rebuildEdges(rows.map(r => r.id));
        }
    }
    draggingIndex.value = null;
    dropTarget.value    = null;
    ghostRow.value      = null;
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup',   onDragEnd);
}

// --- setProperty helpers
const isColorProp = (p) => p && (p.endsWith('_color') || p === 'color');
const spType      = (p) => ['val', 'opacity'].includes(p) ? 'number' : 'text';
const safeHex     = (v) => /^#[0-9a-fA-F]{6}$/.test(v ?? '') ? v : '#000000';
const toId        = (v) => v === '' ? '' : parseInt(v);

function onSPPage(row, val) { row.data = { ...row.data, pageId: val !== '' ? parseInt(val) : '', objectId: '', property: '', value: '' }; }
function onSPObj (row, val) { row.data = { ...row.data, objectId: val !== '' ? parseInt(val) : '', property: '', value: '' }; }
function onSPProp(row, val) { row.data = { ...row.data, property: val, value: '' }; }

// --- string builder / icon picker
const showPicker = ref(false);
const pickerRow  = ref(null);

function openPicker(row) {
    pickerRow.value  = row;
    showPicker.value = true;
}

function onPickerUpdate(val) {
    if (pickerRow.value) set(pickerRow.value, 'value', val);
}

// --- moodlight helpers
const moodHex = (d) => {
    const h = n => Math.round(Math.max(0, Math.min(255, n || 0))).toString(16).padStart(2, '0');
    return `#${h(d.r ?? 255)}${h(d.g ?? 0)}${h(d.b ?? 0)}`;
};
function onMoodColor(row, hex) {
    row.data = { ...row.data,
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16),
    };
}

// --- templateRef helpers
function onTpPage(row, val) {
    row.data = { ...row.data, templatePageId: val === '' ? '' : parseInt(val), templateObjectId: '' };
}
</script>

<style scoped>
select, input[type="text"], input[type="number"] {
    background: var(--ui-bg-input);
    color: #e2e8f0;
    border: 1px solid color-mix(in srgb, var(--ui-active) 30%, transparent);
    border-radius: 4px;
    padding: 2px 4px;
    font-size: 11px;
}
select:focus, input:focus { outline: none; border-color: var(--ui-active); }
input.color-pick {
    width: 28px; height: 28px;
    padding: 2px;
    border: 1px solid color-mix(in srgb, var(--ui-active) 30%, transparent);
    border-radius: 4px;
    background: var(--ui-bg-input);
    cursor: pointer;
    flex-shrink: 0;
}

.trigger-header {
    background: var(--ui-bg-input);
    border: 1px solid color-mix(in srgb, var(--ui-active) 35%, transparent);
}

.cmd-card {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    padding: 0.375rem 0.5rem;
    border-radius: 0.25rem;
    border: 1px solid color-mix(in srgb, var(--ui-active) 25%, transparent);
    background: var(--ui-bg-input);
    transition: border-color 0.15s;
}
.cmd-card:hover {
    border-color: color-mix(in srgb, var(--ui-active) 55%, transparent);
}
.cmd-card--active {
    border-color: var(--ui-active) !important;
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--ui-active) 50%, transparent);
}

.add-cmd-select {
    background: var(--ui-bg-input);
    border: 1px dashed color-mix(in srgb, var(--ui-active) 35%, transparent);
    border-radius: 4px;
    padding: 5px 8px;
    color: var(--ui-active);
}
</style>
